import { makeAutoObservable } from "mobx";
import { isApiErrorResponse } from "../../api/api";
import type { PaymentApiClient } from "../../api/payment.api.client";
import { Form } from "../../components/form/form";
import { convertResponse, formatDataToSubmit } from "../../helpers/serialization";
import type { FileData, PaymentPageCreate, PaymentPageForm, PaymentPageRaw } from "../../types/paymentPage";
import { convertWorkingHoursToSubmit, formatPaymentPageToForm } from "./formatters";
import { isObject } from "./helpers";
import { validationConfig } from "./validation";

export type ModalHeaderForm = {
  backgroundImage: Nullable<string>;
  logo: Nullable<string>;
  switchedOnLogo: boolean;
};

export type ObPaymentPageFormType = {
  form: Form<PaymentPageForm>;
  modalHeaderForm: Form<ModalHeaderForm>;
};

type ObPaymentPageFormState = "loading" | "error" | ObPaymentPageFormType;
export type ObPaymentPageValidationErrors = Record<string, string>;
type SaveStatusType = "idle" | "saving" | "saved" | "error";

export class ObPaymentPageFormStore {
  private _state: ObPaymentPageFormState = "loading";

  private _errors: ObPaymentPageValidationErrors = {};

  private _firstUse = false;

  private _token: Nullable<string> = null;

  private _saveStatus: SaveStatusType = "idle";

  private _api: PaymentApiClient;

  constructor(client: PaymentApiClient) {
    makeAutoObservable(this);
    this._api = client;
  }

  get state(): ObPaymentPageFormState {
    return this._state;
  }

  private set state(state: ObPaymentPageFormState) {
    this._state = state;
  }

  get errors(): ObPaymentPageValidationErrors {
    return this._errors;
  }

  private set errors(errors: ObPaymentPageValidationErrors) {
    this._errors = errors;
  }

  getValidationError(field: string): string | undefined {
    return this.errors[field];
  }

  clearValidationError(field: string): void {
    this.errors[field] &&= "";
  }

  getFieldsWithError(): string[] {
    const serverErrors = Object.keys(this._errors).filter((e) => this._errors[e] !== "");
    let formErrors: string[] = [];
    if (this.state !== "loading" && this.state !== "error") {
      const errors = this.state.form.getErrors();
      formErrors = Object.keys(errors).filter((e) => errors[e] !== "");
    }
    return [...serverErrors, ...formErrors];
  }

  get firstUse(): boolean {
    return this._firstUse;
  }

  private set firstUse(value: boolean) {
    this._firstUse = value;
  }

  get token(): Nullable<string> {
    return this._token;
  }

  private set token(value: Nullable<string>) {
    this._token = value;
  }

  setToken = (token: Nullable<string>): void => {
    this.token = token;
  };

  get saveStatus(): SaveStatusType {
    return this._saveStatus;
  }

  private set saveStatus(value: SaveStatusType) {
    this._saveStatus = value;
  }

  private async _getLogo(url: string): Promise<string> {
    const blob = await this._api.getLogo(url);

    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = (): void => {
        // readAsDataURL always produces a string result
        resolve(typeof reader.result === "string" ? reader.result : "");
      };
      reader.readAsDataURL(blob);
    });
  }

  private async _getFileId(fileData: Nullable<FileData>, file: Nullable<File>): Promise<Nullable<string>> {
    if (fileData) {
      return fileData.id;
    }
    if (file) {
      const data = await this._api.uploadFile(this.token, file);
      return data.id;
    }
    return null;
  }

  async load(id?: string): Promise<void> {
    if (!id) {
      this.firstUse = true;
      const form = new Form(formatPaymentPageToForm(this.firstUse), validationConfig);
      const { backgroundImage, logo: logoUrl, switchedOnLogo } = form.values;
      const modalHeaderForm = new Form<ModalHeaderForm>({ backgroundImage, logo: logoUrl, switchedOnLogo });
      this.state = {
        form,
        modalHeaderForm,
      };
      return;
    }
    try {
      this.state = "loading";
      this.firstUse = false;
      const data = await this._api.getPaymentPage(id);
      const form = new Form(formatPaymentPageToForm(this.firstUse, data), validationConfig);
      const { backgroundImageFileData, logoFileData, switchedOnLogo } = form.values;
      const modalHeaderForm = new Form<ModalHeaderForm>({ backgroundImage: null, logo: null, switchedOnLogo });

      if (backgroundImageFileData) {
        const backgroundImage = await this._getLogo(backgroundImageFileData.url);
        form.onChange("backgroundImage", backgroundImage);
        modalHeaderForm.onChange("backgroundImage", backgroundImage);
      }

      if (logoFileData) {
        const logo = await this._getLogo(logoFileData.url);
        form.onChange("logo", logo);
        modalHeaderForm.onChange("logo", logo);
      }

      this.state = {
        form,
        modalHeaderForm,
      };
    } catch (e) {
      this.state = "error";
      throw e;
    }
  }

  async runCancelScenario(): Promise<void> {
    await this._api.cancelEditingPaymentPage({ token: this.token });
  }

  async runShareScenario(): Promise<void> {
    await this._api.sharePaymentPage({ token: this.token });
  }

  async runPreviewScenario(): Promise<void> {
    await this._api.previewPaymentPage({ token: this.token });
  }

  async runSavedScenario(): Promise<void> {
    await this._api.sendFollowUpSkill({ token: this.token });
  }

  saveData = async (formData: PaymentPageForm): Promise<Nullable<PaymentPageRaw>> => {
    const { publicId, ...data } = formData;
    const {
      workingHours,
      contactEmail,
      contactPhone,
      contactWebsite,
      linkedinLink,
      amount,
      paymentLinkDescription,
      logoFile,
      logoFileData,
      backgroundImageFile,
      backgroundImageFileData,
      currency,
      address,
      paymentPageDescription,
      googleMapsPlaceId,
      businessType,
      facebookLink,
      instagramLink,
    } = data;
    try {
      this.saveStatus = "saving";

      const backgroundImageFileId = await this._getFileId(backgroundImageFileData, backgroundImageFile);
      const logoFileId = await this._getFileId(logoFileData, logoFile);
      const formattedData = formatDataToSubmit<PaymentPageCreate>({
        workingHours: convertWorkingHoursToSubmit(workingHours),
        contactEmail: contactEmail || null,
        contactPhone,
        contactWebsite: contactWebsite || null,
        linkedinLink: linkedinLink || null,
        amount: amount || null,
        paymentLinkDescription,
        logoFileId,
        backgroundImageFileId,
        currency,
        address,
        paymentPageDescription,
        googleMapsPlaceId,
        businessType,
        facebookLink: facebookLink || null,
        instagramLink: instagramLink || null,
      });

      let result;
      if (publicId) {
        result = await this._api.updatePaymentPage(publicId, { ...formattedData, token: this.token });
      } else {
        result = await this._api.createPaymentPage({ ...formattedData, token: this.token });
      }

      if (result) {
        this.saveStatus = "saved";
        return result;
      }
      return null;
    } catch (e) {
      if (isApiErrorResponse(e) && e.status === 413) {
        // eslint-disable-next-line require-unicode-regexp -- ASCII-only regex
        const maxSize = typeof e.data === "string" ? /\d+/u.exec(e.data)?.[0] : undefined;
        const maxMbSize = maxSize && (Number(maxSize) / 1024 / 1024).toFixed();
        this.errors = {
          ...this.errors,
          imageSize: maxMbSize
            ? `The image size is too large. Maximum size is ${maxMbSize}Mb`
            : "The image size is too large.",
        };
        this.saveStatus = "idle";
        return null;
      }

      const apiError = isApiErrorResponse(e) && isObject(e.data) && isObject(e.data.error) ? e.data.error : undefined;

      if (isApiErrorResponse(e) && e.status === 403 && apiError?.message === "Invalid token") {
        this.errors = {
          ...this.errors,
          token: "Authentication error",
        };
        this.saveStatus = "idle";
        return null;
      }

      if (isApiErrorResponse(e) && e.status === 400 && isObject(apiError?.validation_errors)) {
        const errors: Record<string, string> = {};

        for (const [key, value] of Object.entries(
          convertResponse<Record<string, unknown>>(apiError.validation_errors),
        )) {
          if (Array.isArray(value)) {
            errors[key] = value.join("");
          }
        }
        this.errors = {
          ...this.errors,
          ...errors,
        };

        this.saveStatus = "idle";
        return null;
      }

      this.saveStatus = "error";
      throw e;
    }
  };
}
